import { useEffect, useRef, useState } from "react";
import { Archive, Download, Eye, FileUp, Megaphone, Paperclip, Send } from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Loading, SocietyPageHeader } from "../../components/shared";

const EMPTY_NOTICE = {
  type: "NOTICE",
  title: "",
  subject: "",
  body: "",
  category: "General",
  priority: "NORMAL",
  acknowledgement_required: false,
  audience: {
    mode: "ALL_RESIDENTS",
    building_ids: [],
    wing_ids: [],
    unit_ids: [],
  },
};
const DOC_CATEGORIES = [
  "Rules & Policies",
  "Forms",
  "General Documents",
  "Safety/Emergency",
  "Maintenance Information",
  "Other",
];

function AudienceFields({ data, onChange, bootstrap, prefix }) {
  const audience = data.audience;
  const update = (key, values) =>
    onChange({ ...data, audience: { ...audience, [key]: values } });
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <label className="text-sm">
        <span className="mb-1 block font-medium">Audience</span>
        <select
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2"
          data-testid={`${prefix}-audience-mode`}
          onChange={(event) =>
            onChange({
              ...data,
              audience: {
                mode: event.target.value,
                building_ids: [],
                wing_ids: [],
                unit_ids: [],
              },
            })
          }
          value={audience.mode}
        >
          <option value="ALL_RESIDENTS">All residents</option>
          <option value="OWNERS">Owners</option>
          <option value="TENANTS">Tenants</option>
          <option value="SELECTED_BUILDING_WING">
            Selected building / wing
          </option>
          <option value="SELECTED_UNITS">Selected units</option>
        </select>
      </label>
      {audience.mode === "SELECTED_UNITS" && (
        <label className="text-sm">
          <span className="mb-1 block font-medium">Units</span>
          <select
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2"
            data-testid={`${prefix}-audience-units`}
            multiple
            onChange={(event) =>
              update(
                "unit_ids",
                [...event.target.selectedOptions].map((item) => item.value),
              )
            }
            value={audience.unit_ids}
          >
            {bootstrap.units.map((unit) => (
              <option key={unit.unit_id} value={unit.unit_id}>
                {unit.unit_number}
              </option>
            ))}
          </select>
        </label>
      )}
      {audience.mode === "SELECTED_BUILDING_WING" && (
        <>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Buildings</span>
            <select
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2"
              data-testid={`${prefix}-audience-buildings`}
              multiple
              onChange={(event) =>
                update(
                  "building_ids",
                  [...event.target.selectedOptions].map((item) => item.value),
                )
              }
              value={audience.building_ids}
            >
              {bootstrap.buildings.map((building) => (
                <option key={building.building_id} value={building.building_id}>
                  {building.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Wings</span>
            <select
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2"
              data-testid={`${prefix}-audience-wings`}
              multiple
              onChange={(event) =>
                update(
                  "wing_ids",
                  [...event.target.selectedOptions].map((item) => item.value),
                )
              }
              value={audience.wing_ids}
            >
              {bootstrap.wings.map((wing) => (
                <option key={wing.wing_id} value={wing.wing_id}>
                  {wing.name}
                </option>
              ))}
            </select>
          </label>
        </>
      )}
    </div>
  );
}

export default function ResidentCommunications() {
  const [bootstrap, setBootstrap] = useState(null);
  const [notice, setNotice] = useState(EMPTY_NOTICE);
  const [editingNoticeId, setEditingNoticeId] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [preview, setPreview] = useState(null);
  const [upload, setUpload] = useState({ title: "", description: "" });
  const [documentFile, setDocumentFile] = useState(null);
  const [documentDraft, setDocumentDraft] = useState(null);
  const fileRef = useRef(null);

  const load = () =>
    api
      .get("/society-communications/bootstrap")
      .then((response) => setBootstrap(response.data))
      .catch((error) => {
        toast.error(
          error.response?.data?.detail || "Could not load communications",
        );
        setBootstrap({
          notices: [],
          documents: [],
          units: [],
          buildings: [],
          wings: [],
        });
      });

  useEffect(() => {
    load();
  }, []);

  const saveDraft = async () => {
    try {
      const response = editingNoticeId
        ? await api.patch(
            `/society-communications/notices/${editingNoticeId}`,
            notice,
          )
        : await api.post("/society-communications/notices", notice);
      if (attachment) {
        const data = new FormData();
        data.append("file", attachment);
        await api.post(
          `/society-communications/notices/${response.data.notice_id}/attachments`,
          data,
        );
      }
      toast.success(editingNoticeId ? "Draft updated" : "Draft saved");
      setNotice(EMPTY_NOTICE);
      setEditingNoticeId("");
      setAttachment(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save draft");
    }
  };

  const editDraft = (item) => {
    setEditingNoticeId(item.notice_id);
    setNotice({
      type: item.type,
      title: item.title,
      subject: item.subject || "",
      body: item.body || "",
      category: item.category || "General",
      priority: item.priority,
      acknowledgement_required: item.acknowledgement_required,
      audience: item.audience,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const action = async (noticeId, name) => {
    try {
      await api.post(`/society-communications/notices/${noticeId}/${name}`);
      toast.success(`Notice ${name}ed`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update notice");
    }
  };

  const downloadNoticePdf = async (noticeId) => {
    try {
      const response = await api.get(
        `/pdf/society-communications/notices/${noticeId}/pdf`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create the notice PDF");
    }
  };

  const publishDocument = async (document, state, settings = null) => {
    const body =
      state === "PUBLISHED"
        ? {
            state,
            category: settings?.category || "General Documents",
            description: settings?.description || document.description || "",
            audience: settings?.audience || { mode: "ALL_RESIDENTS" },
          }
        : { state };
    try {
      await api.patch(
        `/society-communications/documents/${document.doc_id}/publication`,
        body,
      );
      toast.success(
        state === "PUBLISHED" ? "Document published" : "Document made private",
      );
      setDocumentDraft(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update document");
    }
  };

  const configureDocument = (document) => {
    const current = document.resident_visibility || {};
    setDocumentDraft({
      doc_id: document.doc_id,
      category: current.category || "General Documents",
      description: current.description || document.description || "",
      audience: current.audience || {
        mode: "ALL_RESIDENTS",
        building_ids: [],
        wing_ids: [],
        unit_ids: [],
      },
    });
  };

  const uploadDocument = async () => {
    if (!documentFile) return toast.error("Choose a document");
    const data = new FormData();
    data.append("file", documentFile);
    data.append("title", upload.title || documentFile.name);
    data.append("description", upload.description);
    try {
      await api.post("/society-communications/documents/upload", data);
      toast.success("Document added to the private vault");
      setDocumentFile(null);
      setUpload({ title: "", description: "" });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not upload document");
    }
  };

  if (!bootstrap) return <Loading />;
  return (
    <div data-testid="resident-communications-page">
      <SocietyPageHeader
        icon={Megaphone}
        tone="rose"
        subtitle="Publish resident-safe notices and explicitly share approved Society reference documents."
        title="Notices & Announcements"
      />
      <section
        className="border border-slate-200 bg-white p-5"
        data-testid="notice-create-panel"
      >
        <div className="flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-blue-700" />
          <h2 className="font-display text-xl font-semibold">
            Create notice or circular
          </h2>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block font-medium">Type</span>
            <select
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2"
              data-testid="notice-type"
              onChange={(event) =>
                setNotice({ ...notice, type: event.target.value })
              }
              value={notice.type}
            >
              <option>NOTICE</option>
              <option>CIRCULAR</option>
              <option>ANNOUNCEMENT</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Priority</span>
            <select
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2"
              data-testid="notice-priority"
              onChange={(event) =>
                setNotice({ ...notice, priority: event.target.value })
              }
              value={notice.priority}
            >
              <option>NORMAL</option>
              <option>IMPORTANT</option>
              <option>URGENT</option>
            </select>
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="mb-1 block font-medium">Title</span>
            <Input
              data-testid="notice-title"
              onChange={(event) =>
                setNotice({ ...notice, title: event.target.value })
              }
              value={notice.title}
            />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="mb-1 block font-medium">Subject</span>
            <Input
              data-testid="notice-subject"
              onChange={(event) =>
                setNotice({ ...notice, subject: event.target.value })
              }
              value={notice.subject}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Category</span>
            <Input
              data-testid="notice-category"
              onChange={(event) =>
                setNotice({ ...notice, category: event.target.value })
              }
              value={notice.category}
            />
          </label>
          <label className="flex items-center gap-2 self-end text-sm">
            <input
              checked={notice.acknowledgement_required}
              data-testid="notice-ack-required"
              onChange={(event) =>
                setNotice({
                  ...notice,
                  acknowledgement_required: event.target.checked,
                })
              }
              type="checkbox"
            />
            Acknowledgement required
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="mb-1 block font-medium">Message</span>
            <Textarea
              data-testid="notice-body"
              onChange={(event) =>
                setNotice({ ...notice, body: event.target.value })
              }
              rows={5}
              value={notice.body}
            />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="mb-1 block font-medium">Attachment</span>
            <input
              data-testid="notice-attachment"
              onChange={(event) =>
                setAttachment(event.target.files?.[0] || null)
              }
              type="file"
            />
          </label>
        </div>
        <div className="mt-5">
          <AudienceFields
            bootstrap={bootstrap}
            data={notice}
            onChange={setNotice}
            prefix="notice"
          />
        </div>
        <Button
          className="mt-5"
          data-testid="notice-save-draft"
          onClick={saveDraft}
        >
          <Send className="h-4 w-4" />
          {editingNoticeId ? "Update draft" : "Save draft"}
        </Button>
      </section>
      <section className="mt-8" data-testid="notice-admin-list">
        <h2 className="font-display text-2xl font-semibold">
          Publication register
        </h2>
        <div className="mt-3 space-y-3">
          {bootstrap.notices.map((item) => (
            <article
              className="border border-slate-200 bg-white p-4"
              data-testid={`admin-notice-${item.notice_id}`}
              key={item.notice_id}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold text-blue-700">
                    {item.type} · {item.status}
                  </p>
                  <h3 className="mt-1 font-display text-lg font-semibold">
                    {item.title}
                  </h3>
                  <p className="mt-1 text-xs text-slate-500">
                    {item.audience?.mode?.replaceAll("_", " ")} ·{" "}
                    {item.acknowledgement_count} acknowledgement(s)
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    data-testid={`notice-preview-${item.notice_id}`}
                    onClick={() => setPreview(item)}
                    size="sm"
                    variant="outline"
                  >
                    <Eye className="h-4 w-4" />
                    Preview
                  </Button>
                  <Button
                    data-testid={`notice-letterhead-pdf-${item.notice_id}`}
                    onClick={() => downloadNoticePdf(item.notice_id)}
                    size="sm"
                    variant="outline"
                  >
                    <Download className="h-4 w-4" />
                    Letterhead PDF
                  </Button>
                  {item.status === "DRAFT" && (
                    <>
                      <Button
                        data-testid={`notice-edit-${item.notice_id}`}
                        onClick={() => editDraft(item)}
                        size="sm"
                        variant="outline"
                      >
                        Edit
                      </Button>
                      <Button
                        data-testid={`notice-publish-${item.notice_id}`}
                        onClick={() => action(item.notice_id, "publish")}
                        size="sm"
                      >
                        Publish
                      </Button>
                    </>
                  )}
                  {item.status !== "ARCHIVED" && (
                    <Button
                      data-testid={`notice-archive-${item.notice_id}`}
                      onClick={() => action(item.notice_id, "archive")}
                      size="sm"
                      variant="outline"
                    >
                      <Archive className="h-4 w-4" />
                      Archive
                    </Button>
                  )}
                </div>
              </div>
            </article>
          )) || <p data-testid="notice-admin-empty">No notices yet.</p>}
        </div>
      </section>
      {preview && (
        <section
          className="mt-6 border border-blue-200 bg-blue-50 p-5"
          data-testid="notice-preview-panel"
        >
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-display text-xl font-semibold">
              Preview: {preview.title}
            </h2>
            <Button
              data-testid="notice-preview-close"
              onClick={() => setPreview(null)}
              size="sm"
              variant="outline"
            >
              Close
            </Button>
          </div>
          <p className="mt-3 whitespace-pre-wrap text-sm leading-6">
            {preview.body}
          </p>
        </section>
      )}
      <section
        className="mt-10 border-t border-slate-200 pt-8"
        data-testid="resident-document-admin-panel"
      >
        <div className="flex items-center gap-2">
          <FileUp className="h-5 w-5 text-blue-700" />
          <h2 className="font-display text-2xl font-semibold">
            Resident Document Centre
          </h2>
        </div>
        <p className="mt-2 text-sm text-slate-600">
          Vault records stay private unless explicitly published to an
          authorized resident audience.
        </p>
        <div className="mt-5 grid gap-3 border border-slate-200 bg-white p-4 sm:grid-cols-3">
          <Input
            data-testid="resident-document-upload-title"
            onChange={(event) =>
              setUpload({ ...upload, title: event.target.value })
            }
            placeholder="Document title"
            value={upload.title}
          />
          <Input
            data-testid="resident-document-upload-description"
            onChange={(event) =>
              setUpload({ ...upload, description: event.target.value })
            }
            placeholder="Short description"
            value={upload.description}
          />
          <div className="flex gap-2">
            <input
              className="min-w-0 text-sm"
              data-testid="resident-document-upload-file"
              onChange={(event) =>
                setDocumentFile(event.target.files?.[0] || null)
              }
              ref={fileRef}
              type="file"
            />
            <Button
              data-testid="resident-document-upload-submit"
              onClick={uploadDocument}
              size="sm"
            >
              Upload
            </Button>
          </div>
        </div>
        {documentDraft && (
          <section
            className="mt-5 border border-blue-200 bg-blue-50 p-4"
            data-testid="resident-document-publication-form"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-display text-lg font-semibold">
                Publish selected document
              </h3>
              <Button
                data-testid="resident-document-publication-cancel"
                onClick={() => setDocumentDraft(null)}
                size="sm"
                variant="outline"
              >
                Cancel
              </Button>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="text-sm">
                <span className="mb-1 block font-medium">
                  Resident category
                </span>
                <select
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2"
                  data-testid="resident-document-publication-category"
                  onChange={(event) =>
                    setDocumentDraft({
                      ...documentDraft,
                      category: event.target.value,
                    })
                  }
                  value={documentDraft.category}
                >
                  {DOC_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                <span className="mb-1 block font-medium">
                  Resident description
                </span>
                <Input
                  data-testid="resident-document-publication-description"
                  onChange={(event) =>
                    setDocumentDraft({
                      ...documentDraft,
                      description: event.target.value,
                    })
                  }
                  value={documentDraft.description}
                />
              </label>
            </div>
            <div className="mt-4">
              <AudienceFields
                bootstrap={bootstrap}
                data={documentDraft}
                onChange={setDocumentDraft}
                prefix="resident-document-publication"
              />
            </div>
            <Button
              className="mt-4"
              data-testid="resident-document-publication-submit"
              onClick={() => {
                const document = bootstrap.documents.find(
                  (item) => item.doc_id === documentDraft.doc_id,
                );
                if (document)
                  publishDocument(document, "PUBLISHED", documentDraft);
              }}
            >
              Publish to residents
            </Button>
          </section>
        )}
        <div className="mt-4 space-y-3">
          {bootstrap.documents.map((document) => {
            const published =
              document.resident_visibility?.state === "PUBLISHED";
            return (
              <article
                className="flex flex-wrap items-center justify-between gap-3 border border-slate-200 bg-white p-4"
                data-testid={`resident-document-admin-${document.doc_id}`}
                key={document.doc_id}
              >
                <div>
                  <p className="font-medium">{document.title}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {published
                      ? `Published · ${document.resident_visibility.category}`
                      : "Private vault document"}
                  </p>
                </div>
                <Button
                  data-testid={`resident-document-toggle-${document.doc_id}`}
                  onClick={() => {
                    if (published) publishDocument(document, "PRIVATE");
                    else configureDocument(document);
                  }}
                  size="sm"
                  variant={published ? "outline" : "default"}
                >
                  {published ? "Make private" : "Publish to residents"}
                </Button>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
